import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import NewAddressModal from './NewAddressModal';
import EditAddressModal from './EditAddressModal';
import DeleteAddressModal from './DeleteAddressModal';

import { Button, Text, H3, Panel, Dropdown, Small, Tabs, Table, Box } from '@bigcommerce/big-design';
import { ArrowDropDownIcon, AddIcon, EditIcon, DeleteIcon } from '@bigcommerce/big-design-icons';

import { locationsIndex, locationCreate, locationUpdate, locationDelete } from '../../redux/actions/locations';


const Locations = ({ payload, locations, loadLocations, createLocation, updateLocation, deleteLocation }) => {  
  const [activeAddress, setActiveAddress] = useState(null);
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [showNewAddressModal, setShowNewAddressModal] = useState(false);
  const [showDeleteAddressModal, setShowDeleteAddressModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadLocations(payload);
  }, []);

  useEffect(() => {
    if(!locations.isUpdating){
      setSubmitted(false);
    }
  }, [locations])

  const openNewAddress = () => {
    setShowNewAddressModal(true);
  }

  const openEditAddress = (id) => {
    let address = locations.corporateLocations.locations.find(location => location.id === id);
    setActiveAddress(address);
    setShowEditAddressModal(true);
  }

  const openDeleteAddress = (id) => {
    let address = locations.corporateLocations.locations.find(location => location.id === id);
    setActiveAddress(address);
    setShowDeleteAddressModal(true);
  }

  const actionButtons = (id) => {
    return(
      <>
        <Button
          onClick={() => openEditAddress(id)}
          variant="secondary"
          iconLeft={<EditIcon />}
          disabled={submitted}
          isLoading={submitted}
        >
          Edit
        </Button>
        <Button
          onClick={() => openDeleteAddress(id)}
          variant="secondary"
          actionType="destructive"
          iconLeft={<DeleteIcon />}
          disabled={submitted}
          isLoading={submitted}
        >
          Delete
        </Button>
      </>
    )
  }

  const description = () => {
    return(
      <> 
        <Text>Locations are addresses and details to which purchase orders can be delivered.</Text>
        <Text>The "BigCommerce Locations" panel displays location data from BigCommerce, as it exists in BigCommerce right now. The information is taken live, and will update as BigCommerce is updated. When purchase orders are received by these locations, updates will be made to their inventory levels.</Text>
        <Text>The "Corporate Addresses" panel displays other addresses that can be used for delivery. Click the "New Address" button to create a new corporate address, or you can click the "Edit" button on any row to update that address' information, or the delete button to remove the address.</Text>
        <Text>The address used as your business address in purchase order PDFs is taken from one of these addresses. You can update which of these addresses is used on the <Link to="/settings">Settings page</Link>.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return (
    <>
      <PageHeader stripeBackground='#2c9c7c' helpLink="bigcommerce/manage-locations-and-addresses/" />
      <SettingsPage title="Locations" description={description()}>
        <Panel>
          <H3>BigCommerce Locations</H3>
          <Box
            backgroundColor="primary10"
            border="box"
            borderRadius="normal"
            padding="medium"
          >
            When warehouse receiving orders are created for purchase orders using a shipping address that is a BigCommerce location, that location's inventory will be updated.
          </Box>
          <br/>
          { locations.status === 'success' &&
            <>
              <Table
                columns={[
                  { header: 'Label', hash: 'label', render: ({ label }) => label },
                  { header: 'Enabled', hash: 'enabled', render: ({ enabled }) => enabled?.toString() },
                  { header: 'Street 1', hash: 'address', render: ({ address }) => address.address1 },
                  { header: 'Street 2', hash: 'address', render: ({ address }) => address.address2 },
                  { header: 'City', hash: 'address', render: ({ address }) => address.city },
                  { header: 'Country', hash: 'address', render: ({ address }) => address.country_code },
                  { header: 'State', hash: 'address', render: ({ address }) => address.state },
                  { header: 'Zip', hash: 'address', render: ({ address }) => address.zip },
                ]}
                items={locations.locations}
                stickyHeader
              />
            </>
          }
          { locations.status !== 'success' &&
            <LoadingSpinner size="xxSmall" statusText="Loading locations"/>
          }
        </Panel>
        <Panel>
          <H3>Corporate Addresses</H3>
          <Box
            backgroundColor="primary10"
            border="box"
            borderRadius="normal"
            padding="medium"
          >
            When warehouse receiving orders are created for purchase orders using a shipping address that is a corporate address, inventory counts will NOT be updated.
          </Box>
          <br/>
          { locations.status === 'success' && locations.corporateLocations.locations.length > 0 &&
            <>
              <Table
                columns={[
                  { header: 'Label', hash: 'label', render: ({ label }) => label },
                  { header: 'Name', hash: 'name', render: ({ first_name, last_name }) => [first_name, last_name].join(' ') },
                  { header: 'Street 1', hash: 'street_1', render: ({ street_1 }) => street_1 },
                  { header: 'Street 2', hash: 'street_2', render: ({ street_2 }) => street_2 },
                  { header: 'City', hash: 'city', render: ({ city }) => city },
                  { header: 'Country', hash: 'country', render: ({ country }) => country },
                  { header: 'State', hash: 'state', render: ({ state }) => state },
                  { header: 'Zip', hash: 'zip', render: ({ zip }) => zip },
                  { header: 'Actions', hash: 'actions', render: ({ id }) => actionButtons(id) },
                ]}
                items={locations.corporateLocations.locations}
                stickyHeader
              />
              <hr/>
              <Button
                onClick={() => openNewAddress()}
                iconLeft={<AddIcon />}
                isLoading={submitted}
                disabled={submitted}
              >
                New Address
              </Button>
            </>
          }

          { locations.status !== 'success' &&
            <LoadingSpinner size="xxSmall" statusText="Loading locations"/>
          }

          { locations.status === 'success' && locations.corporateLocations.locations.length === 0 &&
            <Box
              backgroundColor="warning10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              <div className="text-center">
                <Text>No corporate locations set. Create corporate locations to see and manage them here.</Text>
                <Button
                  onClick={() => openNewAddress()}
                  iconLeft={<AddIcon />}
                  isLoading={submitted}
                  disabled={submitted}
                >
                  New Address
                </Button>
              </div>
            </Box>
          }

        </Panel>
      </SettingsPage>
      <EditAddressModal
        key={activeAddress?.id}
        payload={payload}
        showEditAddressModal={showEditAddressModal}
        setShowEditAddressModal={setShowEditAddressModal}
        address={activeAddress}
        updateLocation={updateLocation}
        setSubmitted={setSubmitted}
      />
      <NewAddressModal
        payload={payload}
        showNewAddressModal={showNewAddressModal}
        setShowNewAddressModal={setShowNewAddressModal}
        address={activeAddress}
        createLocation={createLocation}
        setSubmitted={setSubmitted}
      />
      <DeleteAddressModal
        key={activeAddress?.id}
        payload={payload}
        showDeleteAddressModal={showDeleteAddressModal}
        setShowDeleteAddressModal={setShowDeleteAddressModal}
        addressId={activeAddress?.id}
        deleteLocation={deleteLocation}
        setSubmitted={setSubmitted}
      />
    </>
  )

}


const mapStateToProps = state => ({
  locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  loadLocations: (payload) => dispatch(locationsIndex(payload)),
  createLocation: (payload, location) => dispatch(locationCreate(payload, location)),
  updateLocation: (payload, location) => dispatch(locationUpdate(payload, location)),
  deleteLocation: (payload, locationId) => dispatch(locationDelete(payload, locationId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Locations);
